import React from 'react';

import { TenantSettingKey } from '@recurrency/core-api-schema/dist/common/tenantSettings';
import { SearchForecastDTO } from '@recurrency/core-api-schema/dist/search/getSearchForecasts';

import { InventoryUsagePanelContent } from 'components/recipes/sidePane/InventoryUsagePanelContent';
import { LabelValueProperties, LabelValueProperty } from 'components/recipes/sidePane/LabelValueProperties';
import { LocationAvailabilityPanelContent } from 'components/recipes/sidePane/LocationAvailabilityPanelContent';
import { PurchaseOrderLinesPanelContent } from 'components/recipes/sidePane/PurchaseOrderLinesPanelContent';
import { SalesInvoiceLinesPanelContent } from 'components/recipes/sidePane/SalesInvoiceLinesPanelContent';
import { SalesOrderLinesPanelContext } from 'components/recipes/sidePane/SalesOrderLinesPanelContext';
import { SidePane } from 'components/recipes/sidePane/SidePane';
import { StockInheritancePanelContent } from 'components/recipes/sidePane/StockInheritancePanelContent';
import { UnitOfMeasuresPanelContent } from 'components/recipes/sidePane/UnitOfMeasuresPanelContent';

import { splitIdNameStr } from 'utils/formatting';
import { ViewSettingKey } from 'utils/tableAndSidePaneSettings/types';
import { getTenantSetting, isHubAndSpokeEnabled } from 'utils/tenantSettings';

import { ReplenishmentPathsPanelContent } from '../PurchaseTargetsPage/PurchaseTargetLinesPage/sidePane/ReplenishmentPathsPanelContent';

export function ForecastingSidePane({
  record,
  onForecastOverridesUpdated,
}: {
  record: SearchForecastDTO | null;
  onForecastOverridesUpdated: () => void;
}) {
  const hubAndSpokeEnabled = isHubAndSpokeEnabled();
  const showStockInheritance = !!getTenantSetting(TenantSettingKey.FeatureStockInheritance);

  if (!record) {
    return <SidePane title="" content={<div>Please select a table row</div>} />;
  }

  const { foreignId: locationId, name: locationName } = splitIdNameStr(record.location);

  const infoProps: Array<LabelValueProperty | null> = [
    record.short_code ? { label: 'Short Code', value: record.short_code } : null,
    record.primary_supplier ? { label: 'Primary Supplier', value: record.primary_supplier } : null,
  ];

  return (
    <SidePane
      title={`Item #${record.item_id} at ${record.location}`}
      settingKey={ViewSettingKey.ForecastingSidePane}
      description={record.item_name}
      extendedDescription={record.item_desc}
      content={<LabelValueProperties properties={infoProps} />}
      panels={[
        hubAndSpokeEnabled
          ? {
              settingKey: 'replenishmentPath',
              title: 'Replenishment Path',
              content: (
                <ReplenishmentPathsPanelContent
                  itemUid={record.item_uid}
                  itemId={record.item_id}
                  locationId={locationId}
                />
              ),
            }
          : null,
        {
          settingKey: 'usage',
          title: 'Usage & Forecast',
          content: (
            <InventoryUsagePanelContent
              itemId={record.item_id}
              itemUid={record.item_uid}
              locations={[{ foreignId: locationId, name: locationName }]}
              onForecastOverridesUpdated={onForecastOverridesUpdated}
            />
          ),
        },
        showStockInheritance
          ? {
              settingKey: 'stockInheritance',
              title: 'Stock Inheritance',
              content: <StockInheritancePanelContent itemId={record.item_id} itemUid={record.item_uid} />,
            }
          : null,
        {
          settingKey: 'locationAvailability',
          title: 'Location Availability',
          content: <LocationAvailabilityPanelContent itemUid={record.item_uid} />,
        },
        {
          settingKey: 'unitOfMeasure',
          title: 'Unit of Measures',
          content: <UnitOfMeasuresPanelContent itemId={record.item_id} />,
        },
        {
          settingKey: 'purchaseOrders',
          title: 'Purchase Orders',
          content: (
            <PurchaseOrderLinesPanelContent
              itemId={record.item_id}
              companyId={record.company_id}
              locationIds={[locationId]}
            />
          ),
        },
        {
          settingKey: 'salesOrders',
          title: 'Sales Orders',
          content: (
            <SalesOrderLinesPanelContext
              itemId={record.item_id}
              locationIds={[locationId]}
              companyId={record.company_id}
            />
          ),
        },
        {
          settingKey: 'salesHistory',
          title: 'Sales History',
          content: (
            <SalesInvoiceLinesPanelContent
              itemId={record.item_id}
              locationIds={[locationId]}
              companyId={record.company_id}
            />
          ),
        },
      ].filter(Boolean)}
    />
  );
}
